import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTracking, useTransactions } from '../../hooks';
import { Modal, Title } from '@mantine/core';
import { GenericErrorMessage } from '../../../lib/components';
import { Transaction } from '../../shared/types';
import {
  StatusButton,
  TransactionForm,
  Products,
  SubmissionDate,
} from '../Transactions/Components';
import { useNavigate } from 'react-router-dom';
import NewTable from '../../shared/components/NewTable/NewTable';
import { SEARCH_PARAMS_KEYS } from '../../router/searchParamsKeys';
import { uniq } from 'lodash';
import { CellValue } from '../../shared/components/NewTable/useColumns';

export const TransactionsTablePanel = () => {
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { transactions, isLoading } = useTransactions();
  const { trackEvent } = useTracking();
  const { deleteTransaction } = useTransactions();

  const handleCancel = () => setIsImportModalOpen(false);

  useEffect(() => {
    if (isImportModalOpen) {
      trackEvent('Map', {
        step: 'transaction-creation',
        action: 'init',
      });
    }
  }, [trackEvent, isImportModalOpen]);

  const deletion = (row: Transaction) => ({
    label: t('common.delete'),
    onClick: () => deleteTransaction.mutate(row.id || '', {}),
  });

  const edit = (row: Transaction) => ({
    label: t('common.edit'),
    onClick: () => navigate(`/transactions/${row.id}`),
  });

  const view = (row: Transaction) => ({
    label: t('common.view'),
    onClick: () => navigate(`/transactions/${row.id}`),
  });

  const createReference = (row: Transaction) => ({
    label: t('transactions.form.createReference'),
    onClick: () =>
      navigate(`/transactions/new?${SEARCH_PARAMS_KEYS.DDS}=${row.ddsNumbers}`),
  });

  const setActions = (row: Transaction) => {
    const actionsByStatus = {
      DRAFT: [edit(row), deletion(row)],
      SUBMITTED: [view(row), createReference(row)],
      AVAILABLE: [view(row), createReference(row)],
      TO_SUBMIT: [view(row)],
      REJECTED: [view(row)],
      WITHDRAWN: [view(row)],
      ERROR: [view(row)],
    };

    return actionsByStatus[row.status as keyof typeof actionsByStatus] ?? [];
  };

  const columns = [
    {
      value: 'ddsNumbers',
      label: t('transactions.columns.ddsNumber'),
      sort: true,
    },
    { value: 'customId', label: t('transactions.columns.customId') },
    {
      value: 'activityType',
      renderText: (cellValue?: CellValue) =>
        t(`transactions.activity.${cellValue}`),
      label: t('transactions.columns.activity'),
      sort: true,
    },
    {
      value: 'components',
      label: t('transactions.columns.components'),
      sort: true,
      cell: Products,
    },
    {
      value: 'status',
      label: t('transactions.columns.status'),
      sort: true,
      cell: StatusButton,
    },
    {
      value: 'createdAt',
      label: t('transactions.columns.createdAt'),
      sort: true,
      cell: NewTable.DateCell,
    },
    {
      value: 'updatedAt',
      label: t('transactions.columns.submissionDate'),
      sort: true,
      cell: SubmissionDate,
    },
    {
      value: 'actions',
      setActions,
    },
  ];

  const filterOptions = useMemo(
    () => ({
      activity: uniq(transactions?.map((t) => t.activityType)).map(
        (activity) => ({
          label: t(`transactions.activity.${activity}`),
          value: activity,
        })
      ),
      status: uniq(transactions?.map((t) => t.status)).map((status) => ({
        label: t(`transactions.status.${status}`),
        value: status,
      })),
      product: uniq(
        transactions?.flatMap((t) => t.components?.map((c) => c.commodity))
      ).map((product = '') => ({
        label: product,
        value: product,
      })),
    }),
    [transactions]
  );

  const filters = [
    {
      key: SEARCH_PARAMS_KEYS.FILTERS_ACTIVITY,
      label: t('transactions.filters.activity.title'),
      type: 'select' as const,
      options: filterOptions.activity,
      match: (record: Transaction, value: string) =>
        value?.split(',')?.some((activity) => record.activityType === activity),
    },
    {
      key: SEARCH_PARAMS_KEYS.FILTERS_STATUS,
      label: t('transactions.filters.status.title'),
      type: 'select' as const,
      options: filterOptions.status,
      match: (record: Transaction, value: string) =>
        value?.split(',')?.some((status) => record.status === status),
    },
    {
      key: SEARCH_PARAMS_KEYS.FILTERS_PRODUCT,
      label: t('transactions.filters.product.title'),
      type: 'select' as const,
      options: filterOptions.product,
      match: (record: Transaction, value: string) =>
        value
          ?.split(',')
          ?.some((product) =>
            record.components?.some((c) => c.commodity === product)
          ),
    },
  ];

  return (
    <GenericErrorMessage>
      <NewTable<Transaction>
        header={{
          title: t('transactions.title'),
          onAdd: () => navigate('/transactions/new'),
        }}
        columns={columns}
        data={transactions || []}
        loading={isLoading}
        empty={{
          entity: 'transactions',
        }}
        search={{
          keys: ['customId', 'activityType', 'components.commodity', 'status'],
          placeholder:
            'search transactions by id, activity, commodity or status',
        }}
        filters={filters}
      />

      <Modal
        size={500}
        opened={isImportModalOpen}
        onClose={handleCancel}
        title={<Title order={3}>{t('transactions.creation.title')}</Title>}
        centered
      >
        <TransactionForm cancel={handleCancel} />
      </Modal>
    </GenericErrorMessage>
  );
};
